import {
  formatDate as formatDateUtil,
  formatDateTime as formatDateTimeUtil,
  formatDateTimeUTC as formatDateTimeUTCUtil,
  formatDateTimeUTCToLocalTimezone as formatDateTimeUTCToLocalTimezoneUtil,
  formatDateUTC as formatDateUTCUtil,
  formatTime as formatTimeUtil,
  formatTimeUTC as formatTimeUTCUtil,
} from '@kontentino/kontentino-utils/date';
import dayjs, { Dayjs } from 'dayjs';

import store from 'store';
import DateTimeConfig from 'app/config/dateTime';
import { DurationUnitType } from 'dayjs/plugin/duration';

type DayjsDateTimeInput =
  | string
  | number
  | Date
  | dayjs.Dayjs
  | null
  | undefined;

const is12HourFormat = () => !store?.getState().user.useMeridianTime;

export const formatDate = (date: DayjsDateTimeInput) => {
  return formatDateUtil(date, is12HourFormat());
};

export const formatDateTimeUTCToLocalTimezone = (date: DayjsDateTimeInput) => {
  return formatDateTimeUTCToLocalTimezoneUtil(date, is12HourFormat());
};

export const formatDateTimeUTC = (dateTime: DayjsDateTimeInput) => {
  return formatDateTimeUTCUtil(dateTime, is12HourFormat());
};

export const formatDateToMonthAndDay = (date: DayjsDateTimeInput) => {
  const FORMAT = is12HourFormat() ? 'MM/DD' : 'D.M.';

  return dayjs(date).format(FORMAT);
};

export const formatTimeUTC = (
  time: DayjsDateTimeInput,
  format?: string,
): string => {
  return formatTimeUTCUtil(time, is12HourFormat());
};

export const formatTime = (
  time: DayjsDateTimeInput,
  format?: string,
): string => {
  return formatTimeUtil(time, is12HourFormat(), format);
};

export const formatDateTime = (dateTime: DayjsDateTimeInput): string => {
  return formatDateTimeUtil(dateTime, is12HourFormat());
};

export const formatDateUTC = (date: DayjsDateTimeInput): string => {
  return formatDateUTCUtil(date, is12HourFormat());
};

export const formatRawDate = (date: DayjsDateTimeInput) => {
  return dayjs(date).format(DateTimeConfig.DEFAULT_DATE_RAW_FORMAT);
};

const isDateYesterday = (date: Dayjs) => date.isYesterday();
const isDateToday = (date: Dayjs) => date.isToday();
const isDateTomorrow = (date: Dayjs) => date.isTomorrow();

export const getYesterdayTodayTomorrow = (date: Dayjs) => {
  switch (true) {
    case isDateYesterday(date):
      return 'Yesterday';
    case isDateToday(date):
      return 'Today';
    case isDateTomorrow(date):
      return 'Tomorrow';
    default:
      return null;
  }
};

const DateUtils = {
  toMonthYearString(date: Dayjs | null | undefined) {
    return !date ? '' : date.format('MMMM, YYYY');
  },
  toDateString(date: Dayjs | null | undefined) {
    return !date ? '' : date.format(DateTimeConfig.DEFAULT_DATE_RAW_FORMAT);
  },
  toTimeString(date: Dayjs | null | undefined) {
    return !date ? '' : date.format('HH:mm');
  },
  isValid(date: DayjsDateTimeInput) {
    return dayjs(date).isValid();
  },
  isDateRangeMatchesMonth(start: string, end: string) {
    const startDay = dayjs(start);
    const endDay = dayjs(end);

    const startDate = startDay.startOf('day').format('YYYY-MM-DD');
    const endDate = endDay.endOf('day').format('YYYY-MM-DD');

    const isStartDateFirstDayOfMonth =
      startDate === startDay.startOf('month').format('YYYY-MM-DD');
    const isEndDateLastDayOfMonth =
      endDate === endDay.endOf('month').format('YYYY-MM-DD');
    const isSameMonth = startDay.isSame(endDay, 'month');

    return isStartDateFirstDayOfMonth && isEndDateLastDayOfMonth && isSameMonth;
  },
  isDateRangeMatchesThreeMonths(start: string, end: string) {
    const startDay = dayjs(start);
    const endDay = dayjs(end);

    const isStartDateFirstDayOfMonth =
      startDay.startOf('day').format('YYYY-MM-DD') ===
      startDay.startOf('month').format('YYYY-MM-DD');

    const expectedEnd = startDay.add(2, 'month').endOf('month');
    const isEndDateLastDayOfExpectedMonth =
      endDay.endOf('day').format('YYYY-MM-DD') ===
      expectedEnd.format('YYYY-MM-DD');

    return isStartDateFirstDayOfMonth && isEndDateLastDayOfExpectedMonth;
  },
  getThreeMonthsRangeFromAnchor(anchorDate: Dayjs) {
    const startDate = anchorDate.startOf('month');
    const endDate = startDate.add(2, 'month').endOf('month');

    return { startDate, endDate };
  },
  humanizeDiff(
    date: Dayjs | null | undefined,
    config?: {
      unit?: DurationUnitType;
    },
  ) {
    if (!date) return '';

    const diff = date.diff(dayjs(), 'minute');

    return dayjs.duration(diff, config?.unit ?? 'minutes').humanize();
  },
  diffInDays(date: Dayjs | null | undefined) {
    return !date
      ? 0
      : date.isValid()
      ? date.diff(dayjs().startOf('day'), 'day')
      : 0;
  },
  toCompactRelative(date: DayjsDateTimeInput) {
    if (!date) return '';
    const target = dayjs(date);
    if (!target.isValid()) return '';

    const now = dayjs();
    const minutes = now.diff(target, 'minute');
    if (minutes < 1) return 'just now';
    if (minutes < 60)
      return `${minutes} ${minutes === 1 ? 'minute' : 'minutes'} ago`;

    const hours = now.diff(target, 'hour');
    if (hours < 24) return `${hours} ${hours === 1 ? 'hour' : 'hours'} ago`;

    const days = now.diff(target, 'day');
    if (days < 30) return `${days} ${days === 1 ? 'day' : 'days'} ago`;

    const months = now.diff(target, 'month');
    return `${months} ${months === 1 ? 'month' : 'months'} ago`;
  },
};

export default DateUtils;
